import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  Snackbar,
  Alert,
  Skeleton
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  AccountTree as AccountTreeIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import { useAuth } from '@/store/authStore';
import StatsCards from './components/StatsCards';
import CompanyProfileSection from './components/CompanyProfileSection';
import LeadershipSection from './components/LeadershipSection';
import OrganizationTree from './components/OrganizationTree';
import WorkingHoursSection from './components/WorkingHoursSection';
import TeamsSection from './components/TeamsSection';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `org-tab-${index}`,
    'aria-controls': `org-tabpanel-${index}`,
  };
}

const OrganizationSetupPage: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();

  const {
    organizationDetails,
    stats,
    loading,
    detailsLoading,
    error,
    successMessage,
    fetchAllData,
    resetMessages
  } = useOrganizationSetupStore();

  // Check if user can edit (HR or Admin)
  const canEdit = user?.role === 'HR' || user?.role === 'Admin';

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    resetMessages();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)'
            : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            opacity: 0.1,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {detailsLoading ? (
            <>
              <Skeleton variant="text" width={300} height={50} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
              <Skeleton variant="text" width={200} height={30} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            </>
          ) : (
            <>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {organizationDetails?.company_name || 'Your Organization'}
              </Typography>
              {organizationDetails?.tagline && (
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  {organizationDetails.tagline}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Stats Section */}
      <StatsCards stats={stats} loading={loading} />

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="organization setup tabs"
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          <Tab
            icon={<BusinessIcon />}
            iconPosition="start"
            label="Company Profile"
            {...a11yProps(0)}
          />
          <Tab
            icon={<GroupsIcon />}
            iconPosition="start"
            label="Teams"
            {...a11yProps(1)}
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Leadership Team"
            {...a11yProps(2)}
          />
          <Tab
            icon={<AccountTreeIcon />}
            iconPosition="start"
            label="Organization Chart"
            {...a11yProps(3)}
          />
          <Tab
            icon={<ScheduleIcon />}
            iconPosition="start"
            label="Working Hours"
            {...a11yProps(4)}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <CompanyProfileSection canEdit={canEdit} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TeamsSection canEdit={canEdit} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <LeadershipSection canEdit={canEdit} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <OrganizationTree />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <WorkingHoursSection canEdit={canEdit} isAdmin={user?.role === 'Admin'} />
      </TabPanel>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationSetupPage;
